import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$, fmtLbs, today, Modal, useForm } from '../components/helpers';

const STATUSES = ['Pending', 'Ordered', 'In Transit', 'Received'];
const EMPTY = { po_number: '', supplier: '', supplier_address: '', supplier_city: '', supplier_phone: '', material: '', resin_id: '', qty: '', unit: 'lbs', unit_price: '', expected_date: '', terms: '', attn: '', notes: '', status: 'Pending' };

// Company info — update these to match Pak 1 Plastics
const COMPANY = {
  name: 'PAK 1 PLASTICS',
  address: '401 E THOMAS ST',
  city: 'WILKES BARRE, PA  18705',
  phone: 'Tel: 917-544-3581',
};

function pad(str, len, right = false) {
  str = String(str);
  if (right) return str.padStart(len);
  return str.padEnd(len);
}

function generateTxt(po) {
  const LINE = '--------------------------------------------------------------------------------';
  const totalAmount = (Number(po.qty) * Number(po.unit_price)).toFixed(2);
  const formattedTotal = Number(totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 });
  const unitPriceStr = Number(po.unit_price).toFixed(4);
  const date = po.expected_date
    ? new Date(po.expected_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
    : '';

  const qtyStr = pad(Number(po.qty).toLocaleString() + ' ' + (po.unit || 'EA'), 14);
  const materialStr = pad(po.material, 28);
  const packStr = pad('', 14);
  const priceStr = pad(unitPriceStr, 10, true);
  const amountStr = pad(formattedTotal, 12, true);

  const lines = [
    '',
    pad('PURCHASE ORDER', 50) + 'No. ' + po.po_number,
    '',
    pad(COMPANY.name, 80),
    pad(COMPANY.address, 80),
    pad(COMPANY.city, 80),
    pad(COMPANY.phone, 80),
    '',
    '   To  ' + pad(po.supplier, 32) + 'Ship To  ' + COMPANY.name,
    '       ' + pad(po.supplier_address || '', 32) + '         ' + COMPANY.address,
    '       ' + pad(po.supplier_city || '', 32) + '         ' + COMPANY.city,
    '',
    po.supplier_phone ? '       Tel: ' + po.supplier_phone : '',
    LINE,
    '   DATE : ' + new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) + '  TERMS: ' + pad(po.terms || '', 20) + 'DELIVERY ON : ' + date,
    LINE,
    '   QUANTITY     ITEM-SIZE                PACK              PRICE        AMOUNT',
    LINE,
    '   ' + qtyStr + materialStr + packStr + priceStr + amountStr,
    po.notes ? '         ' + po.notes : '',
    '',
    pad('', 69) + '------------',
    '',
    pad('TOTAL', 78, true) + '  $' + formattedTotal,
    '',
    po.attn ? '               ATTN: ' + po.attn : '               ATTN: TODD S',
    '               PLEASE VERIFY ORDER RECEIVED',
    '',
  ];

  return lines.join('\n');
}

function downloadTxt(po) {
  const content = generateTxt(po);
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'PO_' + po.po_number + '.txt';
  a.click();
  URL.revokeObjectURL(url);
}

export default function PurchaseOrders() {
  const [items, setItems] = useState([]);
  const [resins, setResins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, set, setForm] = useForm(EMPTY);

  async function load() {
    const [{ data: po }, { data: raw }] = await Promise.all([
      supabase.from('purchase_orders').select('*').order('created_at', { ascending: false }),
      supabase.from('raw_materials').select('id,name,on_hand').order('name'),
    ]);
    setItems(po || []);
    setResins(raw || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const totalCost = (Number(form.qty) || 0) * (Number(form.unit_price) || 0);

  function openAdd() { setForm(EMPTY); setEditing(null); setModal('form'); }
  function openEdit(item) {
    const qty = item.qty_lbs || 0;
    const unitPrice = qty > 0 && item.total_cost ? (item.total_cost / qty).toFixed(4) : '';
    setForm({
      po_number: item.po_number, supplier: item.supplier,
      supplier_address: item.supplier_address || '',
      supplier_city: item.supplier_city || '',
      supplier_phone: item.supplier_phone || '',
      material: item.material, resin_id: item.resin_id || '',
      qty, unit: 'lbs', unit_price: unitPrice,
      expected_date: item.expected_date || '',
      terms: item.terms || '', attn: item.attn || '',
      notes: item.notes || '', status: item.status,
    });
    setEditing(item); setModal('form');
  }

  async function save() {
    if (!form.po_number || !form.supplier || !form.material || !form.qty) {
      alert('PO number, supplier, material, and qty are required.'); return;
    }
    const qty = Number(form.qty);
    const unitPrice = Number(form.unit_price) || 0;
    const total = qty * unitPrice;
    const wasReceived = editing && editing.status !== 'Received' && form.status === 'Received';
    const payload = {
      po_number: form.po_number.trim(),
      supplier: form.supplier.trim(),
      supplier_address: form.supplier_address.trim(),
      supplier_city: form.supplier_city.trim(),
      supplier_phone: form.supplier_phone.trim(),
      material: form.material.trim(),
      resin_id: form.resin_id || null,
      qty_lbs: qty,
      total_cost: total,
      expected_date: form.expected_date || null,
      terms: form.terms.trim(),
      attn: form.attn.trim(),
      notes: form.notes.trim(),
      status: form.status,
      received_at: form.status === 'Received' ? new Date().toISOString() : null,
    };
    if (editing) {
      await supabase.from('purchase_orders').update(payload).eq('id', editing.id);
      if (wasReceived && form.resin_id) {
        const resin = resins.find(r => r.id === form.resin_id);
        if (resin) {
          await supabase.from('raw_materials').update({ on_hand: Number(resin.on_hand) + qty, updated_at: today() }).eq('id', resin.id);
          await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: resin.id, item_name: resin.name, adjustment: qty, reason: 'PO received: ' + form.po_number });
        }
      }
    } else {
      await supabase.from('purchase_orders').insert(payload);
    }
    setModal(null); load();
  }

  async function del(id) {
    if (!window.confirm('Delete this PO?')) return;
    await supabase.from('purchase_orders').delete().eq('id', id);
    load();
  }

  const statusBadge = { Pending: 'badge-warn', Ordered: 'badge-blue', 'In Transit': 'badge-blue', Received: 'badge-ok' };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Purchase Orders</div>
          <div className="page-sub">Inbound supplier orders — export as .txt to send to suppliers</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ New PO</button>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr><th>PO #</th><th>Supplier</th><th>Material</th><th>Qty</th><th>Unit Price</th><th>Total</th><th>Status</th><th>Expected</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr className="empty-row"><td colSpan={9}>Loading...</td></tr>}
            {!loading && items.length === 0 && <tr className="empty-row"><td colSpan={9}>No purchase orders yet.</td></tr>}
            {items.map(item => {
              const unitPrice = item.qty_lbs > 0 && item.total_cost ? item.total_cost / item.qty_lbs : null;
              return (
                <tr key={item.id}>
                  <td className="mono">{item.po_number}</td>
                  <td>{item.supplier}</td>
                  <td>{item.material}</td>
                  <td className="mono">{fmtLbs(item.qty_lbs)}</td>
                  <td className="mono">{unitPrice ? fmt$(unitPrice) + '/lb' : '—'}</td>
                  <td className="mono">{item.total_cost ? fmt$(item.total_cost) : '—'}</td>
                  <td><span className={`badge ${statusBadge[item.status] || 'badge-gray'}`}>{item.status}</span></td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{item.expected_date || '—'}</td>
                  <td>
                    <div className="flex-row">
                      <button className="btn btn-sm" onClick={() => downloadTxt({ ...item, qty: item.qty_lbs, unit: 'lbs', unit_price: unitPrice || 0 })} title="Download .txt">↓ TXT</button>
                      <button className="btn btn-sm" onClick={() => openEdit(item)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => del(item.id)}>Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal === 'form' && (
        <Modal title={editing ? 'Edit Purchase Order' : 'New Purchase Order'} onClose={() => setModal(null)} onSave={save}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">PO Number *</label>
              <input className="form-input mono" value={form.po_number} onChange={set('po_number')} placeholder="e.g. PO-2026-001" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier *</label>
              <input className="form-input" value={form.supplier} onChange={set('supplier')} placeholder="e.g. LyondellBasell" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Supplier Address</label>
              <input className="form-input" value={form.supplier_address} onChange={set('supplier_address')} placeholder="e.g. 248 Wyandanch Ave" />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier City / State / Zip</label>
              <input className="form-input" value={form.supplier_city} onChange={set('supplier_city')} placeholder="e.g. West Babylon, NY 11704" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Supplier Phone</label>
              <input className="form-input" value={form.supplier_phone} onChange={set('supplier_phone')} placeholder="e.g. 631-253-3333" />
            </div>
            <div className="form-group">
              <label className="form-label">Attn</label>
              <input className="form-input" value={form.attn} onChange={set('attn')} placeholder="e.g. John Smith" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Material / Item *</label>
              <input className="form-input" value={form.material} onChange={set('material')} placeholder="e.g. PP Homopolymer" />
            </div>
            <div className="form-group">
              <label className="form-label">Link to Inventory</label>
              <select className="form-input" value={form.resin_id} onChange={set('resin_id')}>
                <option value="">— Select for auto-increment —</option>
                {resins.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <div className="form-hint">When marked Received, qty added automatically</div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Quantity *</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-input mono" type="number" min="0" value={form.qty} onChange={set('qty')} placeholder="0" style={{ flex: 1 }} />
                <select className="form-input" value={form.unit} onChange={set('unit')} style={{ width: 80 }}>
                  <option>lbs</option>
                  <option>units</option>
                  <option>boxes</option>
                  <option>rolls</option>
                  <option>sheets</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Price per {form.unit} ($)</label>
              <input className="form-input mono" type="number" min="0" step="0.0001" value={form.unit_price} onChange={set('unit_price')} placeholder="0.0000" />
            </div>
          </div>
          {form.qty && form.unit_price && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
              <span style={{ color: 'var(--muted)' }}>Total PO cost</span>
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 15 }}>{fmt$(totalCost)}</span>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Terms</label>
              <input className="form-input" value={form.terms} onChange={set('terms')} placeholder="e.g. Net 30" />
            </div>
            <div className="form-group">
              <label className="form-label">Delivery Date</label>
              <input className="form-input" type="date" value={form.expected_date} onChange={set('expected_date')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Notes (appears on PO)</label>
              <input className="form-input" value={form.notes} onChange={set('notes')} placeholder="e.g. 4&quot; Plastic Tray Bulk in Black Repro Without Tab" />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" value={form.status} onChange={set('status')}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {form.status === 'Received' && editing && editing.status !== 'Received' && form.resin_id && (
            <div className="alert alert-ok" style={{ marginTop: 4 }}>
              ✓ Marking as Received will automatically add {Number(form.qty).toLocaleString()} {form.unit} to {resins.find(r => r.id === form.resin_id)?.name || 'the linked inventory'}.
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
